<template>
    <Navbar />
    <div id="content">
        <div id="header">
            <h2>Dívidas</h2>
        </div>

        <div class="card">
            <DataTable v-if="!loading" :value="debts" :rows="25" paginator
                :rowsPerPageOptions="[20, 50, 100, 200, 1000]"
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} a {last} de {totalRecords}" tableStyle="min-width: 50rem">
                <template #header>
                    <div id="filters">
                        <Calendar id="month-select" v-model="selected_date" view="month" dateFormat="mm/yy"
                            placeholder="01/2024" showIcon :showOnFocus="false" iconDisplay="input" />

                        <Dropdown v-model="selected_recurrence" :options="recurrences" optionLabel="name"
                            optionValue="code" placeholder="Recorrência" class="w-full md:w-14rem" required />

                        <Dropdown v-model="selected_status" :options="status" optionLabel="name" optionValue="code"
                            placeholder="Estado" class="w-full md:w-14rem" required />
                        <div>
                            <Button icon="pi pi-search" @click="getDebts" id="search-btn" />
                            <Button icon="pi pi-trash" severity="danger" @click="clearFilters" />
                        </div>
                    </div>

                </template>

                <Column field="debt_id" header="ID" sortable></Column>
                <Column field="debt_description" header="Descrição" sortable></Column>
                <Column field="debt_category_type" header="Recorrência" sortable></Column>
                <Column field="debt_start_at" header="Emissão" sortable></Column>
                <Column field="debt_amount" header="Valor" sortable>
                    <template #body="slotProps">
                        {{ formatCurrency(slotProps.data.debt_amount) }}
                    </template>
                </Column>
                <Column field="debt_status" header="Estado" sortable></Column>
                <Column field="actions" header="Ações">
                    <template #body="slotProps">
                        <Button class="p-button-sm" label="Visualizar" icon="pi pi-eye"
                            @click="view(slotProps.data.debt_id)" />
                    </template>
                </Column>
                <template #empty>
                    <div style="text-align: center;">
                <p>Nenhuma dívida encontrada.</p>
            </div>
                </template>
            </DataTable>
            <div v-else>
                <DataTable :value="skeleton">
                    <Column field="debt_id" header="ID">
                        <template #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="debt_description" header="Descrição">
                        <template #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="debt_category_type" header="Recorrência">
                        <template #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="debt_start_at" header="Emissão">
                        <template #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="debt_amount" header="Valor">
                        <template #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="debt_status" header="Estado">
                        <template #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="actions" header="Ações">
                        <template #body>
                            <Skeleton width="2rem"></Skeleton>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import http from '@/services/http.js'
import Navbar from '@/components/Navbar.vue'
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
import Skeleton from 'primevue/skeleton';
import Row from 'primevue/row';

export default {
    name: 'Debts',
    components: {
        Navbar,
        DataTable,
        Column,
        Button,
        Dropdown,
        Calendar,
        Skeleton,
        Row
    },
    data() {
        return {
            debts: [],
            skeleton: Array.from({ length: 5 }, () => ({})),
            loading: true,
            selected_date: null,
            selected_client: null,
            clients: [],
            selected_recurrence: null,
            recurrences: [
                { name: 'Única', code: 'once' },
                { name: 'Mensal', code: 'recurrent' },
                { name: 'Parcelado', code: 'installment' },
            ],
            selected_status: null,
            status: [
                { name: 'Pendente', code: 'pending' },
                { name: 'Pago', code: 'paid' },
                { name: 'Cancelado', code: 'cancelled' },
            ],
        }
    },
    methods: {
        async getDebts() {
            if (this.selected_date instanceof Date) {
                this.loading = true;
                try {
                    let queryParams = [];

                    const month = String(this.selected_date.getMonth() + 1).padStart(2, '0');
                    const year = String(this.selected_date.getFullYear());
                    queryParams.push(`date_year=${year}&date_month=${month}`);

                    if (this.selected_recurrence) {
                        queryParams.push(`category_type=${this.selected_recurrence}`);
                    }
                    if (this.selected_status) {
                        queryParams.push(`status=${this.selected_status}`);
                    }
                    if (this.selected_client) {
                        queryParams.push(`user_id=${this.selected_client}`);
                    }

                    const queryString = queryParams.join('&');

                    const endpoint = `/debt/me/${queryString ? `?${queryString}` : ''}`;

                    const response = await http.get(endpoint);
                    this.debts = response.data.map(debt => {

                        const formatted_start = new Date(debt.start_at).toLocaleDateString('pt-br', { timeZone: 'UTC' });

                        return {
                            debt_id: debt.id,
                            debt_description: debt.description,
                            debt_amount: debt.amount,
                            debt_category_type: this.formatCategoryType(debt.category_type),
                            debt_start_at: formatted_start,
                            debt_status: this.formatStatusType(debt.status),
                        };
                    });
                } catch (error) {
                    this.$root.toast('Ops...', 'Não foi possível recuperar dívidas', 'error');
                } finally {
                    this.loading = false;
                }
            } else {
                this.$root.toast('Ops...', 'Selecione uma data válida', 'error');
            }
        },
        clearFilters() {
            this.selected_client = null;
            this.selected_recurrence = null;
            this.selected_status = null;
            const currentDate = new Date();
            this.selected_date = currentDate;

            this.getDebts();
        },
        view(id) {
            this.$router.push({ name: 'Detalhes da dívida', params: { id: id } });
        },
        formatCurrency(amount) {
            const formatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
            return formatter.format(amount);
        },
        formatCategoryType(categoryType) {
            switch (categoryType) {
                case 'once':
                    return 'Única';
                case 'recurrent':
                    return 'Mensal';
                case 'installment':
                    return 'Parcelado';
            }
        },
        formatStatusType(statusType) {
            switch (statusType) {
                case 'pending':
                    return 'Pendente';
                case 'paid':
                    return 'Pago';
                case 'cancelled':
                    return 'Cancelado';
            }
        },
    },
    mounted() {
        const currentDate = new Date();
        this.selected_date = currentDate;
        this.getDebts();
    }
}
</script>

<style scoped>
#content {
    padding: 40px 5%;
}

#header {
    display: flex;
    justify-content: left;
    position: relative;
    padding: 0px 16px;
}

#new-debt {
    margin-left: 15px;
    height: 40px;
}

#filters {
    text-align: left;
    display: flex;
    justify-content: left;
}

input,
select,
.p-inputtext,
.p-dropdown,
.p-calendar,
.p-multiselect,
#search-btn {
    margin-right: 10px;
}

@media only screen and (max-width: 800px) {
    #filters {
        display: flex;
        flex-wrap: wrap;
    }

    input,
    select,
    .p-inputtext,
    .p-dropdown,
    .p-calendar,
    .p-multiselect {
        margin-right: 10px;
        margin-bottom: 5px;
    }
}
</style>
